import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PROJECTS, CAREER_PATH, USER_INFO } from '../data';
import { Award, Grid, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { CertificateMatrix } from './CertificateMatrix';

export const AchievementsView = ({ onOpenDossier }) => {
  const [expandedMilestone, setExpandedMilestone] = useState(0);

  const toggleMilestone = (idx) => {
    setExpandedMilestone(expandedMilestone === idx ? null : idx);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-16" data-testid="achievements-view">
      <section className="space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-sky-500/10 border border-sky-500/20 rounded-full">
          <Grid className="w-3 h-3 text-[#00f0ff] shrink-0" />
          <span className="font-mono text-[10px] text-[#00f0ff] tracking-widest font-semibold uppercase">02 // PORTFOLIO MATRICES</span>
        </div>
        <div className="max-w-2xl space-y-4">
          <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight uppercase">
            Engineering the Next Gen Finance Platforms.
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm font-light leading-relaxed">
            Real platforms, real customers, real impact. Selected initiatives across Blockchain, API-led Connected Banking, Cloud, and AI modernization — shipped during 20+ years of enterprise engineering across FAB and HSBC.
          </p>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="bento-grid">
        {PROJECTS.map((project) => (
          <div
            key={project.id}
            className="group glass-card rounded-xl overflow-hidden border border-slate-850 hover:border-[#00f0ff]/40 transition-all duration-300 shadow-lg cursor-pointer flex flex-col justify-between"
            data-testid={`bento-card-${project.id}`}
          >
            <div className="relative aspect-[16/9] w-full overflow-hidden border-b border-slate-900 bg-slate-950">
              <img src={project.image} alt={project.title} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale opacity-75 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700" />
              <div className="absolute top-4 left-4 bg-slate-950/80 backdrop-blur-sm border border-slate-800 text-[9px] font-mono font-medium text-[#00f0ff] px-2.5 py-1 rounded">
                {project.category}
              </div>
            </div>
            <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
              <div className="space-y-2">
                <h3 className="font-display font-bold text-base text-white uppercase tracking-tight group-hover:text-[#00f0ff] transition-colors">{project.title}</h3>
                <p className="text-xs text-slate-400 font-light leading-relaxed">{project.description}</p>
              </div>
              <div className="space-y-4 pt-2">
                <div className="flex flex-wrap gap-1.5">
                  {project.techStack.map((tech) => (
                    <span key={tech} className="font-mono text-[9px] text-slate-400 bg-slate-900 border border-slate-800/80 px-2 py-0.5 rounded">{tech}</span>
                  ))}
                </div>
                {project.metrics && (
                  <div className="grid grid-cols-3 gap-3 border-t border-slate-900/60 pt-3">
                    {project.metrics.map((metric, mIdx) => (
                      <div key={mIdx}>
                        <span className="font-mono text-[8px] text-slate-500 block uppercase tracking-wide">{metric.label}</span>
                        <span className="font-display font-extrabold text-xs text-slate-200 mt-0.5 block leading-none">{metric.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="space-y-6">
        <div className="flex items-center gap-3">
          <Clock className="w-4 h-4 text-[#9d4edd]" />
          <h3 className="font-display font-black text-xl text-white tracking-tight uppercase">Platform Journey Milestones (Chronicle)</h3>
        </div>
        <div className="space-y-4">
          {CAREER_PATH.map((exp, idx) => {
            const isExpanded = expandedMilestone === idx;
            return (
              <div key={idx} className="glass-card rounded-xl border border-slate-850 hover:border-slate-800 transition-all overflow-hidden">
                <button
                  onClick={() => toggleMilestone(idx)}
                  className="w-full text-left p-5 flex items-center justify-between gap-4 focus:outline-none cursor-pointer"
                  data-testid={`milestone-toggle-${idx}`}
                >
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-display font-bold text-sm text-slate-150 uppercase tracking-tight">{exp.role}</span>
                      <span className="font-mono text-[9px] text-[#00f0ff] bg-[#00f0ff]/5 border border-[#00f0ff]/10 px-2 py-0.5 rounded">{exp.period}</span>
                    </div>
                    <div className="font-mono text-xs text-slate-400 font-semibold uppercase">
                      {exp.company} &mdash; <span className="text-slate-500 font-light">{exp.location}</span>
                    </div>
                  </div>
                  <div className="p-1 rounded-full bg-slate-900/50 border border-slate-800 text-slate-300">
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-5 pb-5 pt-1 border-t border-slate-900/40 bg-slate-950/20">
                    <ul className="space-y-2 text-xs text-slate-400 font-light list-disc list-inside leading-relaxed">
                      {exp.details.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="space-y-8">
        <CertificateMatrix certifications={USER_INFO.certifications} />

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-[#39ff14]" />
            <h4 className="font-display font-bold text-sm text-white uppercase tracking-wide">Academic matrix &amp; distinctions</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-5 glass-card rounded-xl border border-slate-850 space-y-3">
              <span className="font-mono text-[9px] text-[#39ff14] bg-[#39ff14]/5 border border-[#39ff14]/20 px-2 py-0.5 rounded font-semibold uppercase">{USER_INFO.education.degree}</span>
              <p className="font-display font-bold text-sm text-white leading-tight uppercase pt-1">{USER_INFO.education.major}</p>
              <p className="text-xs text-slate-400 font-light">
                {USER_INFO.education.school} &mdash; <span className="font-mono text-[10px] text-slate-500">{USER_INFO.education.period}</span>
              </p>
            </div>
            <div className="p-5 glass-card rounded-xl border border-slate-850 space-y-2">
              <div className="font-mono text-[9px] text-slate-500 uppercase tracking-widest">Honors &amp; Awards</div>
              {USER_INFO.awards.map((award, aIdx) => (
                <div key={aIdx} className="text-xs text-slate-300 font-light py-1.5 flex items-center justify-between gap-4 border-b border-slate-900/60 last:border-0">
                  <span>{award.title}</span>
                  <span className="font-mono text-[10px] text-slate-500">{award.org}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="glass-card rounded-xl border border-slate-800/80 p-8 text-center space-y-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#00f0ff]/5 to-transparent pointer-events-none" />
        <div className="max-w-xl mx-auto space-y-3 relative">
          <h4 className="font-display font-black text-xl text-white uppercase tracking-tight">Transcribing Systems Architect?</h4>
          <p className="text-xs text-slate-400 leading-relaxed font-light">
            Download the consolidated Dossier Blueprint (Matrix structure, technical dependencies, node configurations) for local offline archival review.
          </p>
        </div>
        <div className="relative">
          <button
            onClick={onOpenDossier}
            className="px-6 py-3 bg-gradient-to-r from-[#00f0ff] to-[#00aafe] hover:from-[#00aafe] hover:to-[#00f0ff] text-slate-950 font-display font-extrabold text-xs tracking-widest uppercase rounded shadow-lg shadow-sky-500/10 cursor-pointer"
            data-testid="btn-dossier-achievements"
          >
            Consolidate Dossier Matrix
          </button>
        </div>
      </section>
    </motion.div>
  );
};
